// React Components
import React, { Fragment, useEffect, useMemo, useReducer } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Ant Design Components
import {
  Badge, Table, Drawer, Popconfirm, Tabs,
} from 'antd';
import {
  EditOutlined, FilePdfOutlined, LoadingOutlined,
} from '@ant-design/icons';

// Utilities & Helpers
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import Constants, { toISTDate, QUANTITY } from '@Apis/constants';
import Helpers from '@Apis/helpers';

// UI Components
import H3Text from '@Uilib/h3Text';
import H3Image from '@Uilib/h3Image';

// Images
import closeIcon from '@Images/icons/icon-close-blue.png';

// Actions
import GRNActions from '@Actions/grnActions';
import PaymentOutgoingActions from '@Actions/paymentOutgoingActions';
import AttachmentActions from '@Actions/attachmentActions';

// Common Components
import ViewPayment from '@Components/Common/ViewPayment';
import Attachment from '@Components/Common/Attachment';
import ViewCustomFields from '@Components/Common/CustomField/ViewCustomFields';
import BatchesTable from '@Components/Common/BatchesTable';
import ActivityLog from '../../../../Common/ActivityLog';
import WorkFlowTimeLine from '../../../../Common/WorkFlowTimeLine';
import ViewLoadingSkull from '../../../../Common/ViewLoadingSkull';

// GRN & Related
import QualityCheckGRN from '../ViewGRN/QualityCheckGRN';

// Payment Components
import RecordOutgoingPayment from '../../../../Payments/RecordPayment/RecordOutgoingPayment';

// Purchase Components
import DebitNoteForm from '@Components/Purchase/DebitNotes/DebitNoteForm';
import ViewDebitNote from '@Components/Purchase/DebitNotes/ViewDebitNote';

// Styles
import './style.scss';

// Reducer
import { actionType, reducer } from './reducers';

const { TabPane } = Tabs;

const getQcData = (data) => {
  const requiredData = [];
  const productBatches = data?.map((item) => item?.product_batches)?.flat(1);
  const qualityChecks = productBatches?.map((productBatch) => productBatch?.quality_checks)?.flat(1);
  const qualityChecksArr = [];
  qualityChecks?.map((qualityCheck) => {
    data?.map((grnLine) => {
      if (qualityCheck?.entity_line_id === grnLine?.grn_line_id) {
        qualityChecksArr.push({
          ...qualityCheck,
          ordered_qty: grnLine?.ordered_qty,
          product_sku_info: grnLine?.product_sku_info,
          tenant_product_id: grnLine?.tenant_product_id,
          expiry_date: grnLine?.expiry_date,
          available_batches: grnLine?.available_batches,
          tax_info: grnLine?.tax_info,
          uom_info: grnLine?.uom_info,
          uom_list: grnLine?.product_sku_info?.uom_list,
        });
      }
    });
  });
  qualityChecksArr?.map((qualityCheck) => {
    productBatches?.map((productBatch) => {
      if (productBatch?.batch_id === qualityCheck?.batch_id) {
        requiredData.push({
          ...qualityCheck,
          custom_batch_number: productBatch?.custom_batch_number,
          lot_number: productBatch?.lot_number,
          batch_number: productBatch?.batch_number,
          mrp: productBatch?.mrp,
          cost_price: productBatch?.cost_price,
          inventory_location_id: productBatch?.inventory_location_id,
          inventory_location_name: productBatch?.inventory_location_name,
          inventory_location_path: productBatch?.inventory_location_path,
          is_rejected_batch: productBatch?.is_rejected_batch,
          selling_price: productBatch?.selling_price,
          expiry_date: productBatch?.expiry_date,
          quantity: productBatch?.quantity,
        });
      }
    });
  });
  return requiredData;
};

const ViewSTGRN = ({
  selectedGRN, createGRNLoading, updateGRNStatus, callback, updateGRNStatusLoading, deleteGrnLoading, deleteGrn,
  user, getGRNByIdLoading, getGRNById, selectedGrnId, match, isQuickView, history, destinationTenantId, fixedRoute, getAttachmentByIdLoading, updateAttachmentLoading, updateGRNWorkflowStep, updateGRNWorkflowStepLoading, createAndLinkPaymentLoading, unLinkPaymentLoading, selectedAttachment, addGRN, createAndLinkPayment, unLinkPayment, getPaymentsOutgoingSuccess, getGRNSuccess, getAttachmentById, getGRNByIdSuccess, updateAttachment,
}) => {
  const initialState = useMemo(() => {
    return {
      isGetAttachment: false,
      currentTab: '/st-goods-received',
      currentUpdate: '',
      fileList: [],
      isUserReadyForPrefillData: false,
      showDnForm: false,
      showPayment: false,
      openDNModalView: false,
      showViewPayout: false,
    };
  }, []);

  const [state, dispatch] = useReducer(reducer, initialState);

  const setState = (values) => {
    dispatch({
      type: actionType.SET_MULTIPLE_FIELDS,
      payload: values,
    });
  };

  const {
    isGetAttachment, isUserReadyForPrefillData, showPayment, currentUpdate, showDnForm, openDNModalView, selectedDebitNote, showViewPayout, selectedPayment, fileList, currentTab,
  } = state;

  const grnColumns = useMemo(() => [
    {
      title: '',
      responsive: ['xs'],
      render: (item) => (
        <div className="mobile-list__item">
          <Link to={`/inventory/product/view/${item?.product_sku_info?.product_sku_id}`} target="_blank">
            <H3Text text={`SKU# : ${item?.product_sku_info?.internal_sku_code}`} className="mobile-list__item-number" />
          </Link>
          <H3Text text={`Product Name: ${item?.product_sku_info?.product_sku_name}`} className="mobile-list__item-info" />
          <H3Text text={`Transfer Qty: ${QUANTITY(Number(item?.transferred_qty || 0), item?.uom_info?.[0]?.precision)} ${item?.uom_info?.[0]?.uqc?.toProperCase()}`} className="mobile-list__item-info" />
          <H3Text text={`Received Qty: ${QUANTITY(Number(item?.received_qty || 0), item?.uom_info?.[0]?.precision)} ${item?.uom_info?.[0]?.uqc?.toProperCase()}`} className="mobile-list__item-info" />
        </div>
      ),
    },

    {
      title: 'SKU#',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (record) => (
        <Link to={`/inventory/product/view/${record?.product_sku_info?.product_sku_id}`} target="_blank">
          {record?.product_sku_info?.internal_sku_code}
        </Link>
      ),
    },
    {
      title: 'PRODUCT NAME',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (item) => (
        <div style={{ minWidth: '200px' }}>
          {item?.product_sku_info?.product_sku_name}
          <H3Text text={item.remarks} className="table-subscript" />
        </div>
      ),
    },
    {
      title: 'TRANSFER QTY',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (item) => (
        <div>
          <div>
            {`${QUANTITY(
              item?.transferred_qty,
              item?.uom_info?.[0]?.precision,
            )} ${item?.uom_info?.[0]?.uqc?.toProperCase() || '-'}`}
          </div>
          {item?.secondary_uom_qty && (
            <div>
              {`${QUANTITY(
                item?.secondary_uom_qty,
                item?.product_sku_info?.secondary_uom_info?.precision,
              )} ${item?.product_sku_info?.secondary_uom_info?.uqc?.toProperCase() || '-'} (Secondary)`}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'RECEIVED QTY',
      responsive: ['sm', 'md', 'lg', 'xxl'],
      render: (item) => <H3Text text={`${QUANTITY(Number(item?.received_qty || 0), item?.uom_info?.[0]?.precision)} ${item?.uom_info?.[0]?.uqc?.toProperCase()}`} />,
    },
  ], []);

  useEffect(() => {
    getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);

    return () => {
      getGRNByIdSuccess(null);
    };
  }, []);

  useEffect(() => {
    if (selectedGRN && !isUserReadyForPrefillData) {
      setState({
        fileList: selectedGRN?.attachments,
        isUserReadyForPrefillData: true,
      });
    }
  }, [selectedGRN, isUserReadyForPrefillData]);

  useEffect(() => {
    if (selectedAttachment && isGetAttachment && !getAttachmentByIdLoading && !updateAttachmentLoading) {
      setState({
        fileList: selectedAttachment,
        isGetAttachment: false,
      });
    }
  }, [selectedAttachment, isGetAttachment, getAttachmentByIdLoading, updateAttachmentLoading]);

  const handleFileChange = (fileListData) => {
    const fileList = fileListData?.fileList?.map((item) => ({
      ...item,
      url: item?.response?.response?.location || item?.url,
    }));

    const attachments = fileList?.map((attachment) => ({
      url: attachment?.response?.response?.location || attachment?.url, type: attachment.type, name: attachment.name, uid: attachment.uid,
    }));

    if (fileListData?.file.status === 'done') {
      const payload = {
        entity_id: selectedGRN?.grn_id,
        entity_name: 'good_receiving_note',
        attachments,
      };
      updateAttachment(payload, () => {
        getAttachmentById(selectedGRN?.grn_id, 'good_receiving_note', () => {
          setState({
            isGetAttachment: true,
          });
        });
      });
    }
    if (fileListData?.file.status === 'removed') {
      const payload = {
        entity_id: selectedGRN?.grn_id,
        entity_name: 'good_receiving_note',
        attachments,
      };
      updateAttachment(payload, () => {
        getAttachmentById(selectedGRN?.grn_id, 'good_receiving_note', () => {
          setState({
            isGetAttachment: true,
          });
        });
      });
    }

    setState({
      fileList: attachments.length > 0 ? attachments : [],
    });
  };

  const canUserUpdateGrn = () => {
    const selectedSTGRN = selectedGRN?.stock_transfers?.[0];
    return Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.GOOD_RECEIVING, Helpers.permissionTypes.UPDATE).includes(selectedSTGRN?.destination_department_info?.tenant_id);
  };

  const selectedSTGRN = selectedGRN?.stock_transfers?.[0];

  const HideAdhocApprovals = () => {
    if (selectedGRN?.workflow_steps) {
      const steps = Object.values(selectedGRN?.workflow_steps)?.filter(step => step !== null);
      const pendingSteps = steps?.filter(step => step?.status === 'PENDING');
      return steps.length < 10 && pendingSteps.length === steps.length;
    }
    return false;
  };

  const fixedMenuBar = useMemo(() => user?.side_menu_bar_type === 'FIXED', [user]);

  return (
    <Fragment>
      {getGRNByIdLoading === false ? (
        <div className={`view-document__wrapper ${!isQuickView ? 'view-document__wrapper-page' : ''}`}>
          <div className="ant-row">
            <div className={`${fixedMenuBar ? 'ant-col-md-16' : 'ant-col-md-17'} ant-col-xs-24`}>
              <Drawer
                onClose={() => setState({ showDnForm: false })}
                open={showDnForm}
                width="77.5%"
                destroyOnClose

              >
                <div className="custom-drawer__header-wrapper">
                  <div className="custom-drawer__header" style={{ width: '1185px' }}>
                    <H3Text text="Create Debit Note" className="custom-drawer__title" />
                    <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setState({ showDnForm: false })} />
                  </div>
                </div>
                <DebitNoteForm
                  selectedGrnForDN={selectedGRN}
                  callback={() => {
                    setState({ showDnForm: false });
                    getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);
                  }}
                  isQuickView
                />
              </Drawer>
              <Drawer
                open={showPayment}
                width="420px"
                onClose={() => setState({ showPayment: false })}
                destroyOnClose
              >
                <div className="custom-drawer__header-wrapper">
                  <div className="custom-drawer__header" style={{ width: '375px' }}>
                    <H3Text text="Record New Payment" className="custom-drawer__title" />
                    <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setState({ showPayment: false })} />
                  </div>
                </div>
                {/* <ListUnusedPayment
                      selectedGRN={selectedGRN}
                      callback={() => {
                        this.setState({ showPayment: false });
                        getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);
                      }}
                    /> */}
                <RecordOutgoingPayment
                  selectedGRN={selectedGRN}
                  callback={() => {
                    setState({ showPayment: false });
                    getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);
                  }}
                />
              </Drawer>
              <div className={`view-left__wrapper ${!isQuickView ? 'is-page-view' : ''}`}>
                <div className="view-document__title">
                  <div className="view-document__title-number-wrapper">
                    <H3Text text={selectedGRN?.grn_number} className="view-document__title-number" />
                    <div className="view-document__title-status" style={{ backgroundColor: Helpers.getStatusColor(selectedGRN?.status).color }}>
                      {selectedGRN?.status?.replaceAll('_', ' ')}
                    </div>
                  </div>
                  <div className="view-document__title-actions">
                    <div className="action-buttons">
                      {selectedGRN?.status.toUpperCase() === 'DRAFT' && canUserUpdateGrn() && (
                        <H3Text
                          text={(
                            <Link to={`/purchase/goods-receiving/update/${selectedGRN?.grn_id}/type=st`}>
                              <EditOutlined />
                            </Link>
                          )}
                          onClick={() => {
                            getGRNSuccess(null);
                          }}
                          className="action-button"
                        />
                      )}
                      <H3Text
                        text={(
                          <a
                            style={{ padding: '8px 0px 8px 0px' }}
                            target="_blank"
                            href={`${Constants.GRN}/download?grn_id=${selectedGRN?.grn_id}&tenant_id=${selectedGRN?.tenant_info?.tenant_id}&user_id=${user?.user_id}`}
                            rel="noreferrer"
                          >
                            <FilePdfOutlined />
                          </a>
                        )}
                        className="action-button"
                      />
                      {
                        canUserUpdateGrn()
                        && selectedGRN?.status.toUpperCase() !== 'VOID'
                        && (
                          <Popconfirm
                            placement="topRight"
                            title="Are you sure you want to cancel this GRN?"
                            onConfirm={() => {
                              setState({ currentUpdate: 'VOID' });
                              updateGRNStatus({
                                grn_id: selectedGRN?.grn_id,
                                grn_tenant_id: selectedGRN?.grn_tenant_id,
                                grn_entity_type: 'PURCHASE_ORDER',
                                status: 'VOID',
                              }, () => {
                                setState({ currentUpdate: '' });
                                getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);
                                if (callback) callback();
                              });
                            }}
                            okText="Yes"
                            cancelText="No"
                          >
                            <H3Text
                              text={(
                                <div>
                                  {(currentUpdate === 'VOID' && updateGRNStatusLoading)
                                    ? <LoadingOutlined /> : (
                                      <span>Cancel</span>
                                    )}
                                </div>
                              )}
                              className="action-button action-button-big"
                            />
                          </Popconfirm>
                        )}
                      {canUserUpdateGrn() && selectedGRN?.status.toUpperCase() === 'DRAFT'
                        && (
                          <Popconfirm
                            placement="topRight"
                            title="Are you sure you want to issue this GRN?"
                            onConfirm={() => {
                              setState({ currentUpdate: 'ISSUED' });
                              updateGRNStatus({
                                grn_id: selectedGRN?.grn_id,
                                status: 'ISSUED',
                              }, () => {
                                setState({ currentUpdate: '' });
                                getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);
                                if (callback) callback();
                              });
                            }}
                            okText="Yes"
                            cancelText="No"
                          >
                            <H3Text
                              text={(
                                <div>
                                  {(currentUpdate === 'ISSUED' && updateGRNStatusLoading)
                                    ? <LoadingOutlined /> : (
                                      <span>Issue GRN</span>
                                    )}
                                </div>
                              )}
                              className="action-button action-button-big"
                            />
                          </Popconfirm>
                        )}
                    </div>
                    {Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.GOOD_RECEIVING,
                      Helpers.permissionTypes.DELETE).includes(selectedGRN?.tenant_info?.tenant_id)
                      && selectedGRN?.status.toUpperCase() === 'VOID'
                      && (
                        <div className="action-buttons">
                          <Popconfirm
                            placement="topRight"
                            title="Are you sure you want to delete this GRN permanently?"
                            onConfirm={() => {
                              setState({ currentUpdate: 'DELETE' });
                              deleteGrn(selectedGRN?.grn_tenant_id, selectedGRN?.grn_id, () => {
                                setState({ currentUpdate: '' });
                                if (!fixedRoute) history.push('/purchase/goods-receiving/');
                                if (callback) callback();
                              });
                            }}
                            okText="Yes"
                            cancelText="No"
                          >
                            <H3Text
                              text={(
                                <div>
                                  {(currentUpdate === 'DELETE' && deleteGrnLoading) ? <LoadingOutlined /> : (
                                    <span>Delete</span>
                                  )}
                                </div>
                              )}
                              className="action-button action-button-big"
                            />
                          </Popconfirm>
                        </div>
                      )}
                  </div>
                </div>
                <div className="document-header">
                  <div className="ant-row">
                    <div className="ant-col-md-12">
                      <div className="document-header__field">
                        <H3Text
                          text="Source"
                          className="document-header__field-name"
                        />
                        <H3Text text={`${selectedSTGRN?.source_department_info?.tenant_name} (${selectedSTGRN?.source_department_info?.department_name})`} className="document-header__field-value" />
                      </div>
                    </div>
                    <div className="ant-col-md-12">
                      <div className="document-header__field">
                        <H3Text
                          text="Destination"
                          className="document-header__field-name"
                        />
                        <H3Text text={`${selectedSTGRN?.destination_department_info?.tenant_name} (${selectedSTGRN?.destination_department_info?.department_name})`} className="document-header__field-value" />
                      </div>
                    </div>
                    <div className="ant-col-md-12">
                      <div className="document-header__field">
                        <H3Text text="GRN Date" className="document-header__field-name" />
                        <H3Text
                          text={
                            toISTDate(selectedGRN?.grn_date_time).format('DD/MM/YYYY')
                          }
                          className="document-header__field-value"
                        />
                      </div>
                    </div>
                    <div className="ant-col-md-12">
                      <div className="document-header__field">
                        <H3Text text="DC#" className="document-header__field-name" />
                        <H3Text text={selectedGRN?.invoice_number || '-'} className="document-header__field-value" />
                      </div>
                    </div>
                    <div className="ant-col-md-12">
                      <div className="document-header__field">
                        <H3Text text="DC Date" className="document-header__field-name" />
                        <H3Text text={toISTDate(selectedGRN?.invoice_date).format('DD/MM/YYYY')} className="document-header__field-value" />
                      </div>
                    </div>
                    <ViewCustomFields
                      customFields={selectedGRN?.custom_fields}
                      wrapperClass="document-header__field"
                      labelClass="document-header__field-name"
                      valueClass="document-header__field-value"
                    />
                  </div>
                </div>
                <br />
                <Tabs
                  activeKey={currentTab}
                  onChange={(key) => {
                    setState({ currentTab: key });
                  }}
                  mode="horizontal"
                  type="card"
                >
                  <TabPane tab="Goods Received" key="/st-goods-received" />
                  {getQcData(selectedGRN?.grn_lines)?.length > 0 && Helpers.getPermission(Helpers.permissionEntities.QUALITY_CHECKS, Helpers.permissionTypes.READ, user) && (
                    <TabPane
                      tab={(
                        <div>
                          Quality Checks &nbsp;
                          {getQcData(selectedGRN?.grn_lines)?.filter((item) => item.status !== 'COMPLETED')?.length ? <Badge count={getQcData(selectedGRN?.grn_lines)?.filter((item) => item.status !== 'COMPLETED')?.length} /> : ''}
                        </div>
                      )}
                      key="/grn-quality-checks"
                    />
                  )}
                </Tabs>
                {currentTab === '/st-goods-received' && (
                  <Table
                    bordered
                    showHeader
                    loading={createGRNLoading}
                    size="small"
                    columns={grnColumns}
                    dataSource={selectedGRN?.grn_lines?.map((item) => ({ ...item, key: uuidv4() })) || []}
                    pagination={false}
                    scroll={{ x: true }}
                    expandable={{
                      rowExpandable: (record) => record?.product_batches?.length,
                      expandedRowRender: (record) => (
                        <BatchesTable
                          title="Received Batches"
                          batches={record?.product_batches}
                          uomInfo={record?.uom_info?.[0]}
                          expiryDays={record?.product_sku_info?.expiry_days}
                          showCancel
                          productInfo={record?.product_sku_info}
                        />
                      ),
                    }}
                  />
                )}
                {currentTab === '/grn-quality-checks' && (
                  <QualityCheckGRN
                    callback={() => {
                      setState({ currentTab: '/st-goods-received' });
                      getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.GOOD_RECEIVING, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);
                    }}
                    selectedGRNLines={getQcData(selectedGRN?.grn_lines)}
                  />
                )}

                <div className="view-document__totals-wrapper">
                  <div className="view__document-footer-left">
                    <div className="view__document-tc">
                      {selectedGRN?.remark && (
                        <div className="view__document-tc">
                          <H3Text text="Additional Remarks" className="view__document-tc-title" />
                          <H3Text text={selectedGRN?.remark || ''} className="view__document-tc-body" />
                        </div>
                      )}
                      {selectedGRN?.grn_entity_type === 'PURCHASE_ORDER' && (
                        <div className="grn-totals" />
                      )}
                    </div>
                  </div>
                </div>
                <Attachment
                  fileList={fileList}
                  disableCase={getAttachmentByIdLoading || updateAttachmentLoading}
                  labelClassName="orgFormLabel"
                  inputClassName=""
                  containerClassName="view__document-attachment"
                  handleFileChange={(value) => handleFileChange(value)}
                  updateEnable={Helpers.getPermission(Helpers.permissionEntities.GOOD_RECEIVING, Helpers.permissionTypes.UPDATE, user)}
                />
              </div>
            </div>
            <div className={`${fixedMenuBar ? 'ant-col-md-8' : 'ant-col-md-7'} ant-col-xs-24`}>
              <div className={`view-right__wrapper ${!isQuickView ? 'is-page-view' : ''}`}>
                {selectedGRN?.status !== 'DRAFT' ? (
                  <WorkFlowTimeLine
                    workflowSteps={selectedGRN?.workflow_steps || {}}
                    workflowStepId={selectedGRN?.workflow_step_id}
                    updateWorkflowStep={updateGRNWorkflowStep}
                    loading={updateGRNWorkflowStepLoading}
                    entityId={selectedGRN?.grn_id}
                    entityType="GOOD_RECEIVING_NOTE"
                    HideCase={HideAdhocApprovals()}
                    callback={() => {
                      getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.GOOD_RECEIVING, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);
                    }}
                  />
                ) : ''}
                {selectedGRN && (
                  <ActivityLog
                    entityId={selectedGRN?.grn_id}
                    entityType="good_receiving_note"
                    entityName="Good Receiving Note"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

      ) : (
        // Loader
        <ViewLoadingSkull isQuickView={isQuickView} fixedMenuBar={fixedMenuBar} />

      )}
      <Drawer
        onClose={() => setState({ openDNModalView: false })}
        open={openDNModalView}
        width="820px"
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '775px' }}>
            <H3Text text="View Debit Note" className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setState({ openDNModalView: false })} />
          </div>
        </div>
        <div style={{ marginTop: '-15px' }}>
          <ViewDebitNote
            selectedDNId={selectedDebitNote?.dn_id}
            callback={() => {
              setState({ openDNModalView: false });
              getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);
            }}
            isQuickView
          />
        </div>
      </Drawer>
      <Drawer
        open={showViewPayout}
        onClose={() => setState({ showViewPayout: false })}
        width="880px"
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '825px' }}>
            <H3Text text={`View Payment #${selectedPayment?.payment_id}`} className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setState({ showViewPayout: false })} />
          </div>
        </div>
        <ViewPayment
          selectedPayment={selectedPayment}
          selectedPaymentId={selectedPayment?.payment_id}
          callback={() => {
            setState({ showViewPayout: false });
            getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_ORDER, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);
          }}
        />
      </Drawer>
    </Fragment>
  );
};

const mapStateToProps = ({
  UserReducers, GRNReducers, PaymentOutgoingReducers, AttachmentReducers,
}) => ({
  user: UserReducers.user,
  createGRNLoading: GRNReducers.createGRNLoading,
  updateGRNStatusLoading: GRNReducers.updateGRNStatusLoading,
  createAndLinkPaymentLoading: PaymentOutgoingReducers.createAndLinkPaymentLoading,
  getGRNByIdLoading: GRNReducers.getGRNByIdLoading,
  selectedGRN: GRNReducers.selectedGRN,
  deleteGrnLoading: GRNReducers.deleteGrnLoading,
  unLinkPaymentLoading: PaymentOutgoingReducers.unLinkPaymentLoading,
  selectedAttachment: AttachmentReducers.selectedAttachment,
  getAttachmentByIdLoading: AttachmentReducers.getAttachmentByIdLoading,
  updateAttachmentLoading: AttachmentReducers.updateAttachmentLoading,
  updateGRNWorkflowStepLoading: GRNReducers?.updateGRNWorkflowStepLoading,
});

const mapDispatchToProps = (dispatch) => ({
  getGRNById: (tenantId, grnId) => dispatch(GRNActions.getGRNById(tenantId, grnId)),
  addGRN: (payload, action) => dispatch(GRNActions.createGRN(payload, action)),
  updateGRNStatus: (payload, action) => dispatch(GRNActions.updateGRNStatus(payload, action)),
  deleteGrn: (tenantId, grnId, callback) => dispatch(GRNActions.deleteGrn(tenantId, grnId, callback)),
  createAndLinkPayment: (payload, callback) => dispatch(PaymentOutgoingActions.createAndLinkPayment(payload, callback)),
  unLinkPayment: (grnPaymentId, callback) => dispatch(PaymentOutgoingActions.unLinkPayment(grnPaymentId, callback)),
  getPaymentsOutgoingSuccess: (payments) => dispatch(PaymentOutgoingActions.getPaymentsOutgoingSuccess(payments)),
  getGRNSuccess: (page, poId) => dispatch(GRNActions.getGRNSuccess(page, poId)),
  getAttachmentById: (entityId, entityName, callback) => dispatch(
    AttachmentActions.getAttachmentById(entityId, entityName, callback),
  ),
  getGRNByIdSuccess: (selectedGrn) => dispatch(GRNActions.getGRNByIdSuccess(selectedGrn)),
  updateAttachment: (payload, callback) => dispatch(
    AttachmentActions.updateAttachment(payload, callback),

  ),
  updateGRNWorkflowStep: (payload, callback) => dispatch(GRNActions.updateGRNWorkflowStep(payload, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ViewSTGRN));
